import {App} from "crqlar-react-common/dist/models/app/app.model";
import {Card, Stack, Typography} from "@mui/material";
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import AddIcon from "@mui/icons-material/Add";
import {useMutation} from "react-query";
import {OrganizationAppService} from "crqlar-react-common/dist/services/app/organizationApp.service";
import {LoadingButton} from "@mui/lab";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    app: App;
    onInstall: () => void;
    onUninstall: () => void;
    onOpen: () => void;
    isActive?: boolean;
    isMultiInstance?: boolean;
    instances?: number;
}

const AppListItem = (props: Props) => {

    const readOnlyUser = useReadOnly()
    const {t, language} = useLocalize()

    const requestAccessMut = useMutation(
        ['requestAppAccess', props.app.id],
        async () => {
            try {
                await OrganizationAppService.requestAccess(props.app.id!)
            } catch (e) {
                alert('Error requesting access. Please contact support.')
                throw e
            }
        },
        {
            onSuccess: () => {
                alert('Request sent. Our team will get back to you soon.')
            }
        }
    )

    const imageSource = getScaledUrl(props.app.media, MediaSize.LARGE_600, MediaSize.LARGE_600)
    const imagePreview = (
        <img src={imageSource} style={{
            width: 120,
            height: 120,
            borderRadius: 8,
            objectFit: 'contain'
        }}
        />
    )

    let activateText = t('Activate')
    let enabled = true
    if (props.isActive) {
        activateText = t('Active')
        enabled = false
    }

    return (
        <Card
            sx={{
                width: '100%',
                maxWidth: 720
            }}
        >
            <Stack
                direction={'row'}
                spacing={2}
                sx={{
                    p: 2
                }}
            >
                {imagePreview}

                <Stack gap={0.5}>
                    <Typography variant={'h6'}>
                        {props.app.name}
                    </Typography>

                    <Typography
                        variant={'body2'}>Developer: {props.app.byCrqlar ? 'Crqlar' : props.app.developerName} </Typography>

                    <Typography variant={'subtitle1'} color={CommonThemeColors.sys.text.secondary} sx={{mt: 2}}>
                        {getLocalizedValue(language, props.app.descriptionLocalized)}
                    </Typography>


                    {
                        props.isMultiInstance && (
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                gap={4}
                                sx={{mt: 4}}
                            >
                                <Typography>
                                    {t('Licenses')}: {props.instances}
                                </Typography>

                                {
                                    props.isActive && (
                                        <LoadingButton
                                            disabled={readOnlyUser}
                                            size={'small'}
                                            sx={{
                                                alignSelf: 'flex-start'
                                            }}
                                            onClick={() => {
                                                requestAccessMut.mutate()
                                            }}
                                            startIcon={<AddIcon/>}
                                            loading={requestAccessMut.isLoading}
                                        >
                                            {t('Add more')}
                                        </LoadingButton>
                                    )
                                }
                            </Stack>

                        )
                    }

                </Stack>

                <Stack>
                    <LoadingButton
                        disabled={!enabled || readOnlyUser}
                        variant={enabled ? 'contained' : 'outlined'}
                        onClick={() => {
                            requestAccessMut.mutate()
                        }}
                        loading={requestAccessMut.isLoading}
                    >
                        {activateText}
                    </LoadingButton>
                </Stack>

            </Stack>
        </Card>
    )

}

export default AppListItem;
